/**
 * Path-keyed object helpers shared by the coordinator and useEditor.
 *
 * "Path" means a dotted-string key: `"publicContext.cpvClassification.mainCode"`.
 * Object values along the path are required to be plain objects (no array
 * traversal). Leaves can be any value — including primitives, arrays, or null.
 */

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function hasPath(obj: Partial<Record<string, unknown>>, path: string): boolean {
  const parts = path.split(".");
  let current: unknown = obj;
  for (const part of parts) {
    if (!isPlainObject(current) || !(part in current)) return false;
    current = current[part];
  }
  return true;
}

/**
 * Returns a new object with the given dotted paths removed. Branches that
 * become empty are pruned. The input is not mutated.
 */
export function removePaths(
  fields: Record<string, unknown>,
  paths: string[],
): Record<string, unknown> {
  return paths.reduce(removePath, fields);
}

export function removePath(
  fields: Record<string, unknown>,
  path: string,
): Record<string, unknown> {
  return removePathParts(fields, path.split(".")) ?? {};
}

function removePathParts(
  fields: Record<string, unknown>,
  parts: string[],
): Record<string, unknown> | undefined {
  const [key, ...rest] = parts;
  if (key === undefined || !(key in fields)) return fields;

  const next = { ...fields };
  if (rest.length === 0) {
    delete next[key];
    return Object.keys(next).length > 0 ? next : undefined;
  }

  const value = next[key];
  if (!isPlainObject(value)) {
    delete next[key];
    return Object.keys(next).length > 0 ? next : undefined;
  }

  const child = removePathParts(value, rest);
  if (child === undefined) {
    delete next[key];
  } else {
    next[key] = child;
  }
  return Object.keys(next).length > 0 ? next : undefined;
}

/**
 * Returns a new object containing only the given dotted paths from the input.
 * Paths not present in the input are silently skipped. The input is not
 * mutated.
 *
 * Inverse of {@link removePaths} for the same set of paths:
 *   removePaths(obj, P) ⊕ pickPaths(obj, P) = obj  (modulo path equivalence).
 */
export function pickPaths(
  fields: Record<string, unknown>,
  paths: string[],
): Record<string, unknown> {
  let result: Record<string, unknown> = {};
  for (const path of paths) {
    const value = readPath(fields, path);
    if (value === undefined && !hasPath(fields, path)) continue;
    result = setPath(result, path, value);
  }
  return result;
}

function readPath(obj: Record<string, unknown>, path: string): unknown {
  let current: unknown = obj;
  for (const part of path.split(".")) {
    if (!isPlainObject(current) || !(part in current)) return undefined;
    current = current[part];
  }
  return current;
}

function setPath(
  obj: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const parts = path.split(".");
  const next = { ...obj };
  let cursor = next;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    const existing = cursor[key];
    const child = isPlainObject(existing) ? { ...existing } : {};
    cursor[key] = child;
    cursor = child;
  }
  cursor[parts[parts.length - 1]] = value;
  return next;
}
